import { PASSWORD_MIN_LENGTH } from '@identity/domain/types';
import type { PasswordRuleId } from '@identity/domain/types';

/** A password that has already been validated against the policy. Only the factory can create one. */
export type Password = Readonly<{ value: string }>;

/** Result of checking one policy rule against a candidate value. */
export type PasswordRuleResult = Readonly<{ id: PasswordRuleId; isMet: boolean }>;

/**
 * Checks the password policy one rule at a time, in checklist order.
 *
 * The factory, the checklist UI and the form schema all read it, so the rules are defined once.
 */
export const evaluatePasswordRules = (value: string): PasswordRuleResult[] => [
  { id: 'minLength', isMet: value.length >= PASSWORD_MIN_LENGTH },
  { id: 'uppercase', isMet: /[A-Z]/.test(value) },
  { id: 'digit', isMet: /\d/.test(value) },
];

/** Whether a candidate password meets every rule of the policy. */
export const isPasswordValid = (value: string): boolean => evaluatePasswordRules(value).every((rule) => rule.isMet);

/**
 * Creates a Password. Throws when any policy rule is unmet.
 *
 * It is called from the use case, never from presentation: the password travels as a `string`
 * through the port and becomes a value object where the rule is enforced.
 */
export const createPassword = (value: string): Password => {
  if (!isPasswordValid(value)) {
    throw new Error('Password does not satisfy the policy');
  }

  return Object.freeze({ value });
};
